import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../core/services/modal.service';
import { FactoringScheduleService } from '../../../core/services/factoring-schedule.service';
import { ToastService } from '../../../shared/ui/toast/toast.service';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';

@Component({
  selector: 'app-upload-schedule-modal',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    TextComponent,
    BadgeComponent,
    LucideIconComponent,
  ],
  templateUrl: './upload-schedule-modal.component.html',
})
export class UploadScheduleModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly scheduleService = inject(FactoringScheduleService);
  private readonly toastService = inject(ToastService);

  private readonly modalData = (this.modalService.state()?.data || {}) as {
    hintInvoiceId?: string | null;
    hintInvoiceNumber?: string | null;
    onSuccess?: (scheduleIds: string[]) => void;
  };

  readonly hintInvoiceId = this.modalData?.hintInvoiceId || null;
  readonly hintInvoiceNumber = this.modalData?.hintInvoiceNumber || null;

  selectedFiles = signal<File[]>([]);
  isDragging = signal(false);
  isUploading = signal(false);

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(Array.from(files));
    }
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFiles(Array.from(input.files));
      input.value = '';
    }
  }

  private addFiles(files: File[]): void {
    const pdfs = files.filter(
      (f) =>
        f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfs.length < files.length) {
      this.toastService.show(
        'warning',
        'Only PDF schedule / remittance files are supported.'
      );
    }
    if (pdfs.length === 0) return;

    const current = this.selectedFiles();
    const combined = [...current];
    for (const file of pdfs) {
      if (!combined.some((c) => c.name === file.name && c.size === file.size)) {
        combined.push(file);
      }
    }
    this.selectedFiles.set(combined);
  }

  removeFile(index: number): void {
    const next = [...this.selectedFiles()];
    next.splice(index, 1);
    this.selectedFiles.set(next);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  close(): void {
    this.modalService.close();
  }

  async startProcessing(): Promise<void> {
    const files = this.selectedFiles();
    if (files.length === 0 || this.isUploading()) return;

    this.isUploading.set(true);
    try {
      const ids = await this.scheduleService.uploadAndProcessSchedules(files, {
        hintInvoiceId: this.hintInvoiceId,
      });
      this.toastService.show(
        'success',
        files.length === 1
          ? 'Schedule uploaded — AI extraction running in the background.'
          : `${files.length} schedules uploaded — AI extraction running in the background.`
      );
      this.modalData?.onSuccess?.(ids);
      this.close();
    } catch (err: any) {
      console.error('[UploadScheduleModal] Upload failed:', err);
      this.toastService.show(
        'error',
        err?.message || 'Failed to upload schedule PDF(s).'
      );
    } finally {
      this.isUploading.set(false);
    }
  }
}
